import { buttonBaseClasses } from '@ahamove/web-components/lib/ButtonBase';
import Dialog, { dialogClasses } from '@ahamove/web-components/lib/Dialog';
import DialogContent from '@ahamove/web-components/lib/DialogContent';
import DialogTitle from '@ahamove/web-components/lib/DialogTitle';
import Typography from '@ahamove/web-components/lib/Typography';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React from 'react';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { isServer } from '@/lib/isServer';
import useIsMobile from '@/shared-hooks/useIsMobile';
import type { ServiceModalData } from '../../api/service/types';
import { homeConfig } from '../home/homeConfig';

const ServicesPriceContent = dynamic(
  () => import('../home/ServicesPriceContent'),
  {
    loading: () => <p>Loading</p>,
  }
);

type Props = {
  serviceList: ServiceModalData[];
  availableCities?: AvailableCity[];
  open: boolean;
  handleClose: () => void;
};

export default function ServicesModal({
  serviceList,
  availableCities,
  open,
  handleClose,
}: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation(homeConfig.i18nNamespaces);

  React.useEffect(() => {
    if (!isServer) {
      document.body.style.setProperty(
        'overflow',
        open ? 'hidden' : 'auto',
        'important'
      );
    }
  }, [open]);

  return (
    <Dialog
      maxWidth="xl"
      fullScreen={isMobile ? true : false}
      fullWidth={true}
      open
      scroll="paper"
      onClose={handleClose}
      sx={{
        display: open ? 'block' : 'none',
        borderRadius: 2,
        [`.${dialogClasses.paper}`]: {
          m: 0,
          pb: 0,
          [`> .${buttonBaseClasses.root}`]: {
            right: isMobile ? 'auto' : 8,
            left: isMobile ? 8 : 'auto',
          },
        },
      }}
    >
      <DialogTitle
        pt={(theme) =>
          isMobile
            ? `${theme.spacing(7)} !important`
            : `${theme.spacing(2)} !important`
        }
      >
        <Typography
          variant="h3"
          fontSize={isMobile ? 32 : 48}
          lineHeight={isMobile ? '40px' : '56px'}
          color="grey.900"
          mb={0.75}
        >
          {t('common:price_table.title')}
        </Typography>
        <Typography variant={isMobile ? 'body2' : 'body1'} color="grey.700">
          {t('common:price_table.desc')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pb: 2, borderRadius: 2 }}>
        <ServicesPriceContent
          shouldRefresh={open}
          defaultServiceList={serviceList}
          availableCities={availableCities}
          modal
        />
      </DialogContent>
    </Dialog>
  );
}
